import { BigNumber } from 'ethers';
import Link from 'next/link';
import React from 'react';
import Pill from '@/components/UI/Pill';
import Button, { ButtonType, ButtonSize } from '@/components/UI/buttons/Button';
import { AvatarSize } from '@/components/cards/Avatar';
import AvatarInfo from '@/components/cards/AvatarInfo';
import BidAvatarGroup from '@/components/cards/BidAvatarGroup';
import {
  BudgetDisplayMode,
  BudgetPerAsset,
} from '@/components/display/BudgetPerAsset';
import { DATE_FORMATS } from '@/constants/constants';
import type {
  BidFragment,
  BrandFragment,
  PaymentCurrency_Enum,
} from '@/utilities/__generated__/graphql';
import { getAgreementCapabilities } from '@/utilities/agreement/overpassAgreement';
import { BucketType, createBucketURL } from '@/utilities/createBucketUrl';
import {
  DateThemeMap,
  getHumanizedDate,
  getLocalDate,
} from '@/utilities/dateFormatter';

export type BidTableRowType = {
  id: string;
  Deal: {
    id: string;
    Bids: BidFragment[];
    Brand?: BrandFragment;
    title: string;
    bidding_expiration: string;
    payment_currency: PaymentCurrency_Enum;
    updated_at: string;
  };
};

const BidTableRow = ({ deal }: { deal: BidTableRowType }) => {
  let bidTotal: BigNumber | null;
  const firstBid = deal?.Deal?.Bids?.[0];
  const canDownloadAgreement =
    !!firstBid?.Bids_private_info?.agreementHash &&
    (!!firstBid.LegacyExecution ||
      getAgreementCapabilities(firstBid as any).canGeneratePdf);
  const agreementDownloadUrl = firstBid?.LegacyExecution
    ? `/api/pdf/execution/${firstBid.Bids_private_info?.agreementHash}`
    : `/api/pdf/${firstBid?.Bids_private_info?.agreementHash}`;

  if (firstBid?.Bids_private_info?.bid_amount) {
    bidTotal = deal.Deal.Bids.reduce((state: BigNumber, next: any) => {
      return state.add(
        BigNumber.from(next?.Bids_private_info?.bid_amount || 0)
      );
    }, BigNumber.from(0));
  } else {
    bidTotal = null;
  }

  const localBiddingExpDate = getLocalDate(
    deal.Deal.bidding_expiration,
    DATE_FORMATS.MMMDYhm
  );

  const [humanizedLocalBiddingExpDate, dateEnum] =
    getHumanizedDate(localBiddingExpDate);
  const { theme, pulse } = DateThemeMap[dateEnum];

  return (
    <Link href={`/deal/${deal.Deal.id}`}>
      <div className="grid w-full grid-cols-9 rounded-lg px-0 py-4 hover:cursor-pointer md:grid-cols-12 md:px-4 md:hover:bg-primary-surface">
        <div className="col-span-4 cursor-pointer md:col-span-6 lg:col-span-4">
          {deal.Deal.Brand && (
            <AvatarInfo
              size={AvatarSize.sm}
              imageLink={
                deal.Deal.Brand.username
                  ? `/brand/${encodeURI(deal.Deal.Brand.username)} `
                  : ''
              }
              textLink={`/deal/${deal.id}`}
              image={
                deal.Deal.Brand.username
                  ? createBucketURL(
                      BucketType.Brand,
                      deal.Deal.Brand.username,
                      deal.Deal.Brand.updated_at
                    )
                  : ''
              }
              verified={false}
              alt={
                deal.Deal.Brand.company_name
                  ? `${deal.Deal.Brand.company_name} brand icon`
                  : 'brand icon'
              }
              title={deal.Deal.title}
              primaryInfo={`${
                deal.Deal.Brand?.company_name?.charAt(0)?.toUpperCase() ?? ''
              }${deal.Deal.Brand?.company_name?.slice(1) ?? ''}`}
            />
          )}
        </div>
        <div className=" col-span-4 flex justify-end md:col-span-5 md:justify-start lg:col-span-3">
          <BidAvatarGroup size={AvatarSize.sm} data={deal.Deal.Bids} />
        </div>
        <div className="hidden self-center text-left text-black md:col-span-2 lg:flex">
          <BudgetPerAsset
            paymentCurrency={deal.Deal.payment_currency}
            minBudget={bidTotal ? bidTotal.toString() : undefined}
            royaltyPercentage={
              firstBid?.Bids_private_info?.bid_percentage_amount
            }
            displayMode={BudgetDisplayMode.WITHOUT_TITLE}
            classes="mx-2"
          />
        </div>
        <div className="justify-content-left hidden self-center text-left text-black md:col-span-2 lg:flex">
          <Pill
            theme={theme}
            showDot={true}
            pulse={pulse}
            title={localBiddingExpDate}
          >
            {humanizedLocalBiddingExpDate}
          </Pill>
        </div>
        <div className="col-span-1 hidden h-10 w-10 self-center rounded-full bg-white md:flex">
          {canDownloadAgreement ? (
            <Link href={agreementDownloadUrl}>
              <Button
                classes="inline-flex items-center justify-center text-xs leading-none"
                type={ButtonType.primary}
                size={ButtonSize.sm}
              >
                Download Agreement
              </Button>
            </Link>
          ) : null}
        </div>
      </div>
    </Link>
  );
};
export default BidTableRow;
